import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@availity/element';

import { useAppContext } from '@/context';

const OAAOrganizationTableRow = ({ ...props }) => {
  const { rowNumber, organization = {}, selectable = true } = props;
  const { selectedOAAOrgIds, addSelectedOAAOrgId, removeSelectedOAAOrgId } = useAppContext();
  const { orgName, customerId, appId, lineAddress, address, createDate, taxId, npi } = organization;

  const isChecked = useMemo(() => selectedOAAOrgIds.indexOf(appId) >= 0, [selectedOAAOrgIds, appId]);

  return (
    <TableRow>
      <TableCell>
        <Typography variant="subtitle1">{rowNumber}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle1">{`${orgName} (${customerId})`}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle1">{createDate}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle1">
          <Box>{lineAddress}</Box>
          <Box>{address}</Box>
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle1">{taxId}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle1">{npi}</Typography>
      </TableCell>
      {selectable && (
        <TableCell padding="checkbox" aria-label={`Select or deselect ${orgName} (${appId})`}>
          <Checkbox
            checked={isChecked}
            onChange={(e) => {
              const { checked } = e.target;

              if (checked) {
                addSelectedOAAOrgId(appId);
              } else {
                removeSelectedOAAOrgId(appId);
              }
            }}
          />
        </TableCell>
      )}
    </TableRow>
  );
};

OAAOrganizationTableRow.propTypes = {
  rowNumber: PropTypes.number.isRequired,
  organization: PropTypes.object.isRequired,
  selectable: PropTypes.bool,
};

const OAAOrganizationsTable = ({ ...props }) => {
  const { organizations = [], selectable = true } = props;
  const { setSelectedOAAOrgIds, addSelectedOAAOrgId, removeSelectedOAAOrgId, selectedOAAOrgIds } = useAppContext();

  const [page, setPage] = useState(0);

  // add the org index for visibility
  const organizationsFormatted = useMemo(
    () =>
      organizations.map((organization, index) => {
        const output = organization;
        const orgIndex = index;

        output.orgIndex = orgIndex;

        return output;
      }),
    [organizations]
  );

  const visibleOrganizations = useMemo(
    () => organizationsFormatted.slice(page * 5, page * 5 + 5),
    [page, organizationsFormatted]
  );

  const organizationCount = useMemo(() => organizations.length, [organizations]);
  const numberOfOrgsSelected = useMemo(() => selectedOAAOrgIds?.length, [selectedOAAOrgIds]);

  const handleChangePage = (e, value) => setPage(value);
  const onSelectAllClick = useCallback(
    (e) => {
      if (e.target.checked) {
        const organizationIds = organizations.map((organization) => organization.appId);
        setSelectedOAAOrgIds([...organizationIds]);
      } else {
        setSelectedOAAOrgIds([]);
      }
    },
    [organizations, setSelectedOAAOrgIds]
  );

  return (
    <TableContainer component={Paper}>
      <Table stickyHeader aria-label="OAA Organizations Table">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Organization Name (ID)
                </Box>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Create Date
                </Box>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Address
                </Box>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Tax ID
                </Box>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  NPI
                </Box>
              </Typography>
            </TableCell>
            {selectable && (
              <TableCell padding="checkbox" aria-label="Select or deselect All Organizations">
                <Checkbox
                  color="primary"
                  indeterminate={numberOfOrgsSelected > 0 && numberOfOrgsSelected < organizationCount}
                  checked={organizationCount > 0 && numberOfOrgsSelected === organizationCount}
                  onChange={onSelectAllClick}
                  inputProps={{
                    'aria-label': 'select all rows',
                  }}
                />
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleOrganizations.map((organization) => {
            const { appId, orgIndex } = organization;
            const rowNumber = orgIndex + 1;
            const shouldStartSelected = selectedOAAOrgIds.indexOf(appId) >= 0;

            return (
              <OAAOrganizationTableRow
                key={`${appId}-oaa-org-table-row`}
                rowNumber={rowNumber}
                organization={organization}
                addSelectedOrgId={addSelectedOAAOrgId}
                removeSelectedOrgId={removeSelectedOAAOrgId}
                shouldStartSelected={shouldStartSelected}
                selectable={selectable}
              />
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[]}
              colSpan={12}
              count={organizations?.length || 0}
              rowsPerPage={5}
              page={page}
              onPageChange={handleChangePage}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

OAAOrganizationsTable.propTypes = {
  organizations: PropTypes.array,
  selectable: PropTypes.bool,
};

export default OAAOrganizationsTable;
